import { createPrintJob, type PreviewOptions } from "../../src/index";
import { createInvoiceRows } from "../shared/data";

// The pagination example shows that explicit page breaks still win even when
// paged preview uses estimated page boundaries for other content.
export function createPaginationExample() {
  const job = createPrintJob({
    title: "Manual Pagination Preview",
    pageSize: "A4",
    orientation: "portrait",
    margins: { top: "16mm", right: "12mm", bottom: "16mm", left: "12mm" },
    fontFamily: '"Segoe UI", "PingFang SC", sans-serif'
  });

  job
    .addText("Manual Pagination Preview", {
      tagName: "h1",
      style: {
        margin: 0,
        fontSize: "28px",
        letterSpacing: "0.04em"
      }
    })
    .addHtml(
      `<p style="margin:0;color:#475569;font-size:14px;line-height:1.6;">
        The first page contains the line items. A dedicated page-break element
        moves the summary content to a new page, while paged preview still uses
        real DOM measurement to estimate any remaining automatic breaks.
      </p>`
    )
    .addTable(
      createInvoiceRows(42),
      [
        { key: "sku", label: "SKU", width: "22mm" },
        { key: "name", label: "Description", width: "92mm" },
        { key: "qty", label: "Qty", width: "18mm", align: "center" },
        { key: "total", label: "Total", width: "28mm", align: "right" }
      ],
      {
        striped: true,
        repeatHeaderOnPageBreak: true
      }
    )
    .addPageBreak()
    .addText("Page 2 Summary", {
      tagName: "h2",
      keepTogether: true,
      style: {
        margin: 0,
        fontSize: "22px"
      }
    })
    .addHtml(
      `<div style="display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr));margin-top:8px;">
        <div style="padding:16px;border:1px solid #cbd5e1;border-radius:16px;">
          <strong>Total boxes</strong><br />18
        </div>
        <div style="padding:16px;border:1px solid #cbd5e1;border-radius:16px;">
          <strong>Dispatch lane</strong><br />North Dock A
        </div>
      </div>`,
      { keepTogether: true }
    )
    .addHtml(
      `<p style="margin:0;color:#64748b;font-size:13px;line-height:1.7;">
        This section intentionally starts on a new page to mimic paperwork that
        needs a clean second sheet for approvals, instructions, or signatures.
      </p>`
    );

  const previewOptions: Omit<PreviewOptions, "container"> = {
    mode: "paged",
    paginationStrategy: "estimate",
    showPageGap: true,
    showPageNumbers: true,
    respectPageBreaks: true,
    showAutoPaginationHints: true
  };

  return {
    id: "pagination",
    title: "Manual Pagination Preview",
    description: "Explicit page breaks rendered as a paged preview, while measured pagination still handles natural overflow.",
    job,
    defaultPreviewOptions: previewOptions
  };
}
